import {AbstractControl, AsyncValidatorFn, UntypedFormControl, ValidationErrors, ValidatorFn} from '@angular/forms';
import {DateToolsService} from './date-tools.service';
import {CmsApiService} from './cms-api.service';
import {CommonsService} from './commons.service';
import {MetaField} from './definitions/meta-field';
import {FieldValidation} from './definitions/field-validation';
import {FieldInputType} from './definitions/field-input-type.enum';
import {IsoDateWithPrecision} from './definitions/iso-date-with-precision';
import {FieldDateInfoService} from "./field-date-info.service";

export class FieldValidators {

  static validatePrecisionDate(dateTools: DateToolsService): ValidatorFn {
    return (field: UntypedFormControl): ValidationErrors | null => {
      if (field.value) {
        const validDate: IsoDateWithPrecision = dateTools.validateDate(field.value);
        return !validDate.dd_error_message ? null : {
          validatePrecisionDate: {
            valid: false,
            message: validDate.dd_error_message
          }
        };
      } else {
        return null;
      }
    };
  }

  static validateIdentifier(cms: CmsApiService, originalValue: string): AsyncValidatorFn {
    return (field: AbstractControl): Promise<ValidationErrors | null> => {
      return new Promise(resolve => {
        if (field.value && field.value !== originalValue) {
          cms.identifierExists({identifier: field.value}).then(
            () => {
              // Identifier did not exist
              resolve(null);
            },
            () => {
              // Identifier already exists
              resolve({
                validateIdentifier: {
                  valid: false
                }
              });
            }
          );
        } else {
          resolve(null);
        }
      });
    };
  }

  static validateCompare(
    commons: CommonsService,
    fieldDateInfoService: FieldDateInfoService,
    object: any,
    metaProps: MetaField): ValidatorFn {
    return (field: UntypedFormControl): ValidationErrors | null => {
      const res = {
        valid: true,
        type: 'validCompare',
        message: ''
      };
      let val1: any, val2: any, errorMsg: string, compProp: string, op: string;
      let compareField: string;
      if (metaProps.validation) {
        const validProps: FieldValidation = metaProps.validation;
        compareField = validProps.compare.field;
        op = validProps.compare.comparator;
      } else {
        const dateInfo = fieldDateInfoService.getFieldDateInfo(metaProps)
        compareField = dateInfo?.validation?.compare_field;
        op = dateInfo?.validation?.comparator;
      }
      if (!compareField) {
        return null; // No compare field set
      }
      let parentObject = object;
      if (metaProps.parent_name) {
        parentObject = object[metaProps.parent_name] || object;
      }
      compProp = parentObject[compareField];
      if (compProp === undefined || compProp === null) {
        return null; // Field not set
      }
      if (metaProps.input_type === FieldInputType.DATE_ISO || metaProps.input_type === FieldInputType.DATE_TIME_ISO) {
        const fieldVal = parentObject[metaProps.name];
        if (fieldVal && compProp && field.value) {
          errorMsg = FieldValidators.getDateErrorMsg(op);
          const dateValues = FieldValidators.getDateValues(fieldVal, compProp);
          val1 = dateValues.val1;
          val2 = dateValues.val2;
        }
      } else {
        errorMsg = 'TRANS__VALIDATE__COMPARE_FAILED';
        val1 = field.value;
        val2 = compProp;
      }
      if (val1 !== undefined && val2 !== undefined) {
        res.valid = commons.compareValues(val1, op, val2);
        if (!res.valid) {
          res.message = errorMsg;
        }
      }
      return res.valid ? null : {
        validateCompare: res
      };
    };
  }

  static validateTimecode(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (value === null || value === undefined || value === '') return null;

      let ms: number;

      if (typeof value === 'number') {
        ms = value;
      } else if (typeof value === 'string' && /^\d+$/.test(value.trim())) {
        ms = Number(value.trim());
      } else {
        return {validateTimecode: {message: 'TRANS__VALIDATE__INVALID_TIMECODE'}};
      }

      if (!Number.isInteger(ms) || ms < 0) {
        return {validateTimecode: {message: 'TRANS__VALIDATE__COMPARE_FAILED'}};
      }
      return null;
    };
  }

  static validateTimecodeInterval(
    startKey: string,
    endKey: string,
    validateOnStartControl = false
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const group = validateOnStartControl ? control.parent : control;

      if (!group) {
        return null;
      }

      const start = validateOnStartControl
        ? control.value
        : group.get(startKey)?.value;

      const end = group.get(endKey)?.value;

      if (!Number.isFinite(start) || !Number.isFinite(end)) {
        return null;
      }

      // A start equal to the end is allowed, for segments of zero duration
      return start > end
        ? {
          validateTimecodeInterval: {
            valid: false,
            message: 'TRANS__VALIDATE__RECORDING_SEGMENT_START_EXCEEDS_END',
          },
        }
        : null;
    };
  }

  private static getDateErrorMsg(op: string): string {
    let errorMsg = 'TRANS__VALIDATE__INVALID_DATE';
    if (op === '<' || op === '<=') {
      errorMsg = 'TRANS__VALIDATE__FROM_DATE_EXCEEDS_TO_DATE';
    } else if (op === '>' || op === '>=') {
      errorMsg = 'TRANS__VALIDATE__TO_DATE_LESS_THAN_FROM_DATE';
    }
    return errorMsg;
  }

  private static getDateValues(fieldVal: string, compProp: string): { val1: any, val2: any } {
    // Making sure only the date part is compared, leaving out the time part
    let val1: any, val2: any;
    if (fieldVal.startsWith('-') || compProp.startsWith('-')) {
      // Get year as number
      if (fieldVal.startsWith('-')) {
        val1 = parseInt(fieldVal.substring(0, 5), 10);
      } else {
        val1 = parseInt(fieldVal.substring(0, 4), 10);
      }
      if (compProp.startsWith('-')) {
        val2 = parseInt(compProp.substring(0, 5), 10);
      } else {
        val2 = parseInt(compProp.substring(0, 4), 10);
      }
    } else {
      val1 = fieldVal.substring(0, 10);
      val2 = compProp.substring(0, 10);
    }
    return {val1, val2};
  }
}

